import React, { useEffect, useState, Fragment } from 'react';
import PropTypes from 'prop-types';
import { Card, ListGroup, Badge, Modal, Button } from 'react-bootstrap';
import { Info, CheckCircle, TriangleAlert, CircleX } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useClinicNotificationsBell } from '../../hooks/useClinicNotificationsBell';

const TYPE_CONFIG = {
  info: { icon: Info, colorClass: 'text-primary' },
  success: { icon: CheckCircle, colorClass: 'text-success' },
  warning: { icon: TriangleAlert, colorClass: 'text-warning' },
  error: { icon: CircleX, colorClass: 'text-danger' },
  SENT: { icon: CheckCircle, colorClass: 'text-success' },
  PENDING: { icon: Info, colorClass: 'text-primary' },
  FAILED: { icon: CircleX, colorClass: 'text-danger' },
};

const DEFAULT_CONFIG = { icon: Info, colorClass: 'text-muted' };

const MAX_DISPLAYED = 3;

const RECEPTIONIST_ROLE = 'RECEPTIONIST';
const RECEPTIONIST_ALERT_THRESHOLD = 2;

export default function NotificationsBox({ className }) {
  const navigate = useNavigate();

  const { notifications, refetch: fetchNotifications } = useClinicNotificationsBell();
  const [showReceptionistAlert, setShowReceptionistAlert] = useState(false);

  useEffect(() => {
    const storedRoles = JSON.parse(sessionStorage.getItem('roles')) || [];
    const isReceptionist = storedRoles.includes(RECEPTIONIST_ROLE);
    if (isReceptionist && notifications.length > RECEPTIONIST_ALERT_THRESHOLD) {
      setShowReceptionistAlert(true);
    }
  }, [notifications]);

  useEffect(() => {
    window.addEventListener('notification-read', fetchNotifications);
    return () => window.removeEventListener('notification-read', fetchNotifications);
  }, [fetchNotifications]);

  const displayed = notifications.slice(0, MAX_DISPLAYED);

  const openNotification = (id) => {
    navigate('/clinic-notifications', { state: { selectedId: id } });
  };

  return (
    <Fragment>
      <Modal
        show={showReceptionistAlert}
        onHide={() => setShowReceptionistAlert(false)}
        centered
        data-cy="receptionist-alert-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title className="d-flex align-items-center gap-2">
            <TriangleAlert className="text-warning" size={24} />
            Atenção
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-2">
            Existem <strong>{notifications.length} notificações não lidas</strong> que precisam da
            sua atenção.
          </p>
          <p className="mb-2 text-muted">
            Por favor, verifique as notificações e corrija o que for necessário para que o sistema
            funcione plenamente.
          </p>
          <p className="mb-0 text-muted">
            Após corrigir cada item, lembre-se de marcar a notificação como lida para que ela não
            apareça mais neste alerta.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setShowReceptionistAlert(false)}>
            Fechar
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              setShowReceptionistAlert(false);
              navigate('/clinic-notifications');
            }}
          >
            Verificar notificações
          </Button>
        </Modal.Footer>
      </Modal>

      <Card
        data-cy="notifications-box"
        className={`rounded-4 shadow-lg border-0 ${className ?? ''}`}
      >
        <Card.Header className="border-0 py-3 d-flex align-items-center gap-2">
          <h5 className="mb-0 fw-bold text-muted">Notificações</h5>
          {notifications.length > 0 && (
            <Badge bg="primary" pill>
              {notifications.length === 1 ? '1 não lida' : `${notifications.length} não lidas`}
            </Badge>
          )}
        </Card.Header>

        <div style={{ maxHeight: '28vh', overflowY: 'auto' }}>
          <ListGroup variant="flush">
            {displayed.map((notification) => {
              const key = notification.type ?? notification.status;
              const config = TYPE_CONFIG[key] ?? DEFAULT_CONFIG;
              const NotificationIcon = config.icon;

              return (
                <Fragment key={notification.id}>
                  <ListGroup.Item
                    data-cy="notification-item"
                    action
                    onClick={() => openNotification(notification.id)}
                    className="d-flex align-items-center py-3"
                  >
                    <NotificationIcon
                      className={`me-3 flex-shrink-0 ${config.colorClass}`}
                      size={20}
                    />
                    <div>
                      <p className="mb-1 text-body">
                        <span className="fw-bold">{notification.description}</span>
                      </p>
                      <small className="text-muted">
                        {notification.content?.length > 80
                          ? `${notification.content.slice(0, 80)}...`
                          : notification.content}
                      </small>
                    </div>
                  </ListGroup.Item>
                </Fragment>
              );
            })}
          </ListGroup>
        </div>

        <Card.Body
          data-cy="notifications-empty"
          className="text-center py-5"
          hidden={notifications.length > 0}
        >
          <p className="text-muted mb-0">Tudo em dia! Nenhuma notificação no momento.</p>
        </Card.Body>

        <Card.Body className="text-end py-2">
          <small
            className="text-primary fw-bold"
            role="button"
            onClick={() => navigate('/clinic-notifications')}
          >
            Ver todas
          </small>
        </Card.Body>
      </Card>
    </Fragment>
  );
}

NotificationsBox.propTypes = {
  className: PropTypes.string,
};

NotificationsBox.defaultProps = {
  className: undefined,
};
